/**
 * RapNet Diamond Inventory — Client-Side Search & Display
 * Calls the LA Diamond secure proxy (Vercel) which holds credentials server-side.
 * No API keys are exposed to the browser.
 */
(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────── */
  var PROXY_URL = 'https://la-diamond-rapnet-proxy.vercel.app/api/diamonds';
  var PAGE_SIZE = 24;
  var DEBOUNCE_MS = 500;

  /* ── State ──────────────────────────────────────────── */
  var diamonds = {};       // id → diamond object
  var totalRecords = 0;
  var currentPage = 1;
  var isLoading = false;
  var debounceTimer = null;

  /* ── DOM refs (set in init) ─────────────────────────── */
  var $grid, $pagination, $resultsCount, $sortBy, $modal;

  /* ── Helpers ────────────────────────────────────────── */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function formatPrice(n) {
    if (!n || n <= 0) return 'Price on Request';
    return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  /* Try multiple possible field names (API might use camelCase or snake_case) */
  function field(obj) {
    for (var i = 1; i < arguments.length; i++) {
      if (obj[arguments[i]] !== undefined && obj[arguments[i]] !== null && obj[arguments[i]] !== '') {
        return obj[arguments[i]];
      }
    }
    return '';
  }

  /* ── API ────────────────────────────────────────────── */
  function searchDiamonds() {
    if (isLoading) return;
    isLoading = true;
    showLoading();

    var filters = readFilters();
    var body = {
      page_number: currentPage,
      page_size: PAGE_SIZE,
      search_type: 'WHITE'
    };

    // Merge filters into body
    var keys = Object.keys(filters);
    for (var i = 0; i < keys.length; i++) {
      body[keys[i]] = filters[keys[i]];
    }

    // Ensure at least one filter (API requirement)
    if (!body.size_from && !body.shapes && !body.labs) {
      body.size_from = 0.20;
    }

    fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request: { header: {}, body: body } })
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (data) {
        handleResponse(data);
      })
      .catch(function (err) {
        console.error('RapNet search error:', err);
        showError('Unable to load diamonds. Please try again later.');
      })
      .finally(function () {
        isLoading = false;
      });
  }

  function handleResponse(data) {
    /*
     * RapNet response structure:
     * { response: { header: { error_code, error_message },
     *               body: { search_results: { total_diamonds_found, diamonds_returned },
     *                       diamonds: [...] } } }
     */
    var items = [], total = 0;

    if (data.response && data.response.body) {
      var body = data.response.body;
      items = body.diamonds || [];
      total = (body.search_results && body.search_results.total_diamonds_found) || items.length;

      /* Check for API error */
      if (data.response.header && data.response.header.error_code !== 0) {
        console.error('RapNet API error:', data.response.header.error_message);
        showError('Search error: ' + (data.response.header.error_message || 'Unknown error'));
        return;
      }
    } else if (data.error) {
      showError('Search error. Please try again.');
      console.error('Proxy error:', data.error);
      return;
    } else {
      console.log('Unexpected response format:', JSON.stringify(data).substring(0, 500));
    }

    if (!Array.isArray(items)) items = [];
    totalRecords = total;

    /* Store diamonds by ID */
    diamonds = {};
    for (var i = 0; i < items.length; i++) {
      var d = items[i];
      var id = d.diamond_id || i;
      d._id = String(id);
      diamonds[d._id] = d;
    }

    renderGrid(items);
    renderPagination();
    updateResultsCount();
  }

  /* ── Read Filters ──────────────────────────────────── */
  function readFilters() {
    var f = {};

    /* Shapes */
    var shapes = [];
    $$('.dn-shape-btn.active').forEach(function (el) {
      shapes.push(el.getAttribute('data-shape'));
    });
    if (shapes.length) f.shapes = shapes;

    /* Carat */
    var cMin = parseFloat($('#dnCaratMin') && $('#dnCaratMin').value);
    var cMax = parseFloat($('#dnCaratMax') && $('#dnCaratMax').value);
    if (cMin && cMin > 0) f.size_from = cMin;
    if (cMax && cMax > 0) f.size_to = cMax;

    /* Color */
    var colFrom = $('#dnColorFrom') && $('#dnColorFrom').value;
    var colTo = $('#dnColorTo') && $('#dnColorTo').value;
    if (colFrom) f.color_from = colFrom;
    if (colTo) f.color_to = colTo;

    /* Clarity */
    var clrFrom = $('#dnClarityFrom') && $('#dnClarityFrom').value;
    var clrTo = $('#dnClarityTo') && $('#dnClarityTo').value;
    if (clrFrom) f.clarity_from = clrFrom;
    if (clrTo) f.clarity_to = clrTo;

    /* Cut */
    var cutFrom = $('#dnCutFrom') && $('#dnCutFrom').value;
    var cutTo = $('#dnCutTo') && $('#dnCutTo').value;
    if (cutFrom) f.cut_from = cutFrom;
    if (cutTo) f.cut_to = cutTo;

    /* Price */
    var pMin = parseFloat($('#dnPriceMin') && $('#dnPriceMin').value);
    var pMax = parseFloat($('#dnPriceMax') && $('#dnPriceMax').value);
    if (pMin && pMin > 0) f.price_total_from = pMin;
    if (pMax && pMax > 0) f.price_total_to = pMax;

    /* Lab */
    var labs = [];
    $$('.dn-lab-cb:checked').forEach(function (el) {
      labs.push(el.value);
    });
    if (labs.length) f.labs = labs;

    /* Sort */
    var sortVal = ($('#dnSortBy') && $('#dnSortBy').value) || 'Price-Asc';
    var parts = sortVal.split('-');
    f.sort_by = parts[0];
    f.sort_direction = parts[1] || 'Asc';

    return f;
  }

  /* ── Rendering ─────────────────────────────────────── */
  function renderCard(d) {
    var img = d.image_file || null;
    var shape = d.shape || '';
    var size = d.size || 0;
    var color = d.color || '';
    var clarity = d.clarity || '';
    var cut = d.cut || '';
    var lab = d.lab || '';
    var price = d.total_sales_price || 0;
    var id = d._id;

    /* V360 viewer link as fallback visual indicator */
    var hasV360 = d.diamond_media && d.diamond_media.length > 0;

    var title = capitalize(shape) + ' ' + Number(size).toFixed(2) + 'ct';
    var priceStr = formatPrice(price);

    var imgHtml;
    if (img) {
      imgHtml = '<img src="' + img + '" alt="' + title + '" loading="lazy" onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\'">' +
        '<div class="diamond-placeholder" style="display:none">' + getShapeSVG(shape) + '</div>';
    } else {
      imgHtml = '<div class="diamond-placeholder">' + getShapeSVG(shape) +
        (hasV360 ? '<span class="dn-v360-badge">360&deg;</span>' : '') +
        '</div>';
    }

    return '<div class="product-card diamond-card" data-id="' + id + '">' +
      '<div class="product-img">' +
      imgHtml +
      (lab ? '<span class="diamond-lab-badge">' + lab + '</span>' : '') +
      '</div>' +
      '<div class="product-info">' +
      '<h3 class="product-name">' + title + '</h3>' +
      '<div class="diamond-specs">' +
      (color ? '<span>' + color + '</span>' : '') +
      (clarity ? '<span>' + clarity + '</span>' : '') +
      (cut ? '<span>' + cut + '</span>' : '') +
      '</div>' +
      '<p class="product-price">' + priceStr + '</p>' +
      '</div>' +
      '</div>';
  }

  function renderGrid(items) {
    if (!$grid) return;
    if (!items.length) {
      $grid.innerHTML =
        '<div class="dn-empty">' +
        '<svg width="48" height="48" viewBox="0 0 48 48" fill="none" style="margin-bottom:16px"><path d="M24 4L44 18V30L24 44L4 30V18L24 4Z" stroke="#ccc" stroke-width="2"/><path d="M24 4L44 18L24 32L4 18L24 4Z" fill="#f5f0eb"/></svg>' +
        '<h3>No diamonds found</h3>' +
        '<p>Try adjusting your filters for more results.</p>' +
        '</div>';
      return;
    }
    var html = '';
    for (var i = 0; i < items.length; i++) {
      html += renderCard(items[i]);
    }
    $grid.innerHTML = html;

    /* Attach click handlers */
    var wrapper = document.getElementById('rapnetDiamonds');
    var context = wrapper ? (wrapper.getAttribute('data-context') || 'collection') : 'collection';

    $$('.diamond-card', $grid).forEach(function (card) {
      card.addEventListener('click', function () {
        var id = this.getAttribute('data-id');
        if (!id || !diamonds[id]) return;

        if (context === 'collection') {
          /* Navigate to inline PDP-style detail view on same page */
          try {
            sessionStorage.setItem('rapnet_diamond_' + id, JSON.stringify(diamonds[id]));
          } catch (e) { /* storage full — detail view will API-fetch */ }
          var detailUrl = window.location.pathname + '?diamond=' + id;
          history.pushState({ diamond: id }, '', detailUrl);
          showInlineDetail(diamonds[id]);
        } else {
          /* Wizard context — keep modal behavior */
          showDetail(diamonds[id]);
        }
      });
    });
  }

  function renderPagination() {
    if (!$pagination) return;
    var totalPages = Math.ceil(totalRecords / PAGE_SIZE);
    if (totalPages <= 1) { $pagination.innerHTML = ''; return; }

    var html = '';
    if (currentPage > 1) {
      html += '<a href="#" class="pagination-prev" data-page="' + (currentPage - 1) + '">&laquo; Previous</a>';
    }

    var start = Math.max(1, currentPage - 3);
    var end = Math.min(totalPages, currentPage + 3);

    if (start > 1) {
      html += '<a href="#" class="pagination-num" data-page="1">1</a>';
      if (start > 2) html += '<span class="pagination-dots">&hellip;</span>';
    }

    for (var i = start; i <= end; i++) {
      if (i === currentPage) {
        html += '<span class="pagination-num active">' + i + '</span>';
      } else {
        html += '<a href="#" class="pagination-num" data-page="' + i + '">' + i + '</a>';
      }
    }

    if (end < totalPages) {
      if (end < totalPages - 1) html += '<span class="pagination-dots">&hellip;</span>';
      html += '<a href="#" class="pagination-num" data-page="' + totalPages + '">' + totalPages + '</a>';
    }

    if (currentPage < totalPages) {
      html += '<a href="#" class="pagination-next" data-page="' + (currentPage + 1) + '">Next &raquo;</a>';
    }

    $pagination.innerHTML = html;

    /* Attach click handlers */
    $$('[data-page]', $pagination).forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var page = parseInt(this.getAttribute('data-page'));
        if (page && page !== currentPage) {
          currentPage = page;
          searchDiamonds();
          /* Scroll to top of grid */
          var top = $grid.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      });
    });
  }

  function updateResultsCount() {
    if (!$resultsCount) return;
    if (totalRecords === 0) {
      $resultsCount.textContent = 'No diamonds found';
    } else {
      var start = (currentPage - 1) * PAGE_SIZE + 1;
      var end = Math.min(currentPage * PAGE_SIZE, totalRecords);
      $resultsCount.textContent = 'Showing ' + start + '–' + end + ' of ' + totalRecords.toLocaleString() + ' diamonds';
    }
  }

  function showLoading() {
    if (!$grid) return;
    var html = '';
    for (var i = 0; i < 6; i++) {
      html += '<div class="product-card dn-skeleton"><div class="product-img"></div>' +
        '<div class="product-info"><div class="dn-skel-line" style="width:' + (60 + Math.random() * 20) + '%"></div>' +
        '<div class="dn-skel-line" style="width:' + (40 + Math.random() * 20) + '%"></div>' +
        '<div class="dn-skel-line" style="width:' + (30 + Math.random() * 15) + '%"></div></div></div>';
    }
    $grid.innerHTML = html;
    if ($pagination) $pagination.innerHTML = '';
    if ($resultsCount) $resultsCount.textContent = 'Loading diamonds...';
  }

  function showError(msg) {
    if ($grid) {
      $grid.innerHTML =
        '<div class="dn-empty dn-error">' +
        '<p>' + msg + '</p>' +
        '</div>';
    }
    if ($resultsCount) $resultsCount.textContent = '';
    if ($pagination) $pagination.innerHTML = '';
    isLoading = false;
  }

  /* ── Detail Modal ──────────────────────────────────── */
  function showDetail(d) {
    var shape = d.shape || '';
    var size = d.size || 0;
    var color = d.color || '';
    var clarity = d.clarity || '';
    var cut = d.cut || '';
    var polish = d.polish || '';
    var symmetry = d.symmetry || '';
    var lab = d.lab || '';
    var certNum = d.cert_num || '';
    var price = d.total_sales_price || 0;
    var img = d.image_file || null;
    var depth = d.depth_percent;
    var table = d.table_percent;
    var fluorescence = d.fluor_intensity || '';
    var stockNum = d.stock_num || '';
    var v360 = (d.diamond_media && d.diamond_media.length > 0) ? d.diamond_media[0].url : null;

    /* Build measurements string from individual fields */
    var measurements = '';
    if (d.meas_length && d.meas_width && d.meas_depth) {
      measurements = d.meas_length + ' x ' + d.meas_width + ' x ' + d.meas_depth + ' mm';
    }

    var title = capitalize(shape) + ' ' + Number(size).toFixed(2) + 'ct Diamond';

    /* Image — prefer image, then V360 iframe, then SVG placeholder */
    var $imgEl = $('#dnModalImage');
    if ($imgEl) {
      if (img) {
        $imgEl.innerHTML = '<img src="' + img + '" alt="' + title + '" onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\'">' +
          '<div class="diamond-placeholder" style="display:none">' + getShapeSVG(shape) + '</div>';
      } else if (v360) {
        $imgEl.innerHTML = '<iframe src="' + v360 + '" style="width:100%;height:100%;border:none;" allowfullscreen></iframe>';
      } else {
        $imgEl.innerHTML = '<div class="diamond-placeholder">' + getShapeSVG(shape) + '</div>';
      }
    }

    /* Title & Price */
    var $title = $('#dnModalTitle');
    if ($title) $title.textContent = title;

    var $price = $('#dnModalPrice');
    if ($price) $price.textContent = formatPrice(price);

    /* Badges */
    var $badges = $('#dnModalBadges');
    if ($badges) {
      var bHtml = '';
      if (lab) bHtml += '<span class="dn-badge">' + lab + ' Certified</span>';
      if (certNum) bHtml += '<span class="dn-badge">#' + certNum + '</span>';
      $badges.innerHTML = bHtml;
    }

    /* Specs table */
    var $specs = $('#dnModalSpecs tbody');
    if ($specs) {
      var rows = [
        ['Shape', capitalize(shape)],
        ['Carat', Number(size).toFixed(2)],
        ['Color', color],
        ['Clarity', clarity],
        ['Cut', cut],
        ['Polish', polish],
        ['Symmetry', symmetry],
        ['Fluorescence', fluorescence],
        ['Depth', depth ? depth + '%' : ''],
        ['Table', table ? table + '%' : ''],
        ['Measurements', measurements],
      ];
      var specHtml = '';
      for (var i = 0; i < rows.length; i++) {
        if (rows[i][1]) {
          specHtml += '<tr><th>' + rows[i][0] + '</th><td>' + rows[i][1] + '</td></tr>';
        }
      }
      $specs.innerHTML = specHtml;
    }

    /* Inquire link */
    var $inquire = $('#dnModalInquire');
    if ($inquire) {
      var subject = encodeURIComponent('Inquiry: ' + title);
      var body = encodeURIComponent(
        'I am interested in the following diamond:\n\n' +
        title + '\n' +
        'Color: ' + color + ' | Clarity: ' + clarity + ' | Cut: ' + cut + '\n' +
        (lab ? 'Lab: ' + lab : '') + (certNum ? ' | Cert: ' + certNum : '') + '\n' +
        (stockNum ? 'Stock #: ' + stockNum + '\n' : '') +
        '\nPlease provide more details and pricing.'
      );
      $inquire.href = '/pages/help/contact?subject=' + subject + '&body=' + body;
    }

    /* Show modal */
    $modal = $('#dnModal');
    if ($modal) {
      $modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDetail() {
    if ($modal) {
      $modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  /* ── Shape SVG placeholder ─────────────────────────── */
  function getShapeSVG(shape) {
    var paths = {
      round: '<circle cx="40" cy="40" r="30" fill="none" stroke="#ccc" stroke-width="1.5"/><circle cx="40" cy="40" r="18" fill="none" stroke="#ddd" stroke-width="1"/><line x1="40" y1="10" x2="40" y2="70" stroke="#eee" stroke-width="0.5"/>',
      oval: '<ellipse cx="40" cy="40" rx="20" ry="30" fill="none" stroke="#ccc" stroke-width="1.5"/>',
      emerald: '<rect x="15" y="12" width="50" height="56" rx="4" fill="none" stroke="#ccc" stroke-width="1.5"/><rect x="22" y="19" width="36" height="42" fill="none" stroke="#ddd" stroke-width="0.8"/>',
      princess: '<rect x="12" y="12" width="56" height="56" fill="none" stroke="#ccc" stroke-width="1.5" transform="rotate(0 40 40)"/>',
      cushion: '<rect x="12" y="12" width="56" height="56" rx="12" fill="none" stroke="#ccc" stroke-width="1.5"/>',
      pear: '<path d="M40 10 C55 25 58 50 40 70 C22 50 25 25 40 10Z" fill="none" stroke="#ccc" stroke-width="1.5"/>',
      marquise: '<path d="M40 8 C58 20 58 60 40 72 C22 60 22 20 40 8Z" fill="none" stroke="#ccc" stroke-width="1.5"/>',
      radiant: '<rect x="14" y="10" width="52" height="60" rx="2" fill="none" stroke="#ccc" stroke-width="1.5"/>',
      asscher: '<rect x="14" y="14" width="52" height="52" rx="2" fill="none" stroke="#ccc" stroke-width="1.5"/><rect x="21" y="21" width="38" height="38" fill="none" stroke="#ddd" stroke-width="0.8"/>',
      heart: '<path d="M40 68 C20 50 6 36 12 24 C18 12 30 12 40 24 C50 12 62 12 68 24 C74 36 60 50 40 68Z" fill="none" stroke="#ccc" stroke-width="1.5"/>',
    };
    var key = (shape || 'round').toLowerCase();
    var path = paths[key] || paths.round;
    return '<svg viewBox="0 0 80 80" width="80" height="80">' + path + '</svg>';
  }

  /* ── Filter Events ─────────────────────────────────── */
  function debouncedSearch() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      currentPage = 1;
      searchDiamonds();
    }, DEBOUNCE_MS);
  }

  function setupFilterListeners() {
    /* Shape buttons */
    $$('.dn-shape-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        this.classList.toggle('active');
        debouncedSearch();
      });
    });

    /* Range inputs (carat, price) */
    ['dnCaratMin', 'dnCaratMax', 'dnPriceMin', 'dnPriceMax'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) {
        el.addEventListener('change', debouncedSearch);
        el.addEventListener('keyup', function (e) {
          if (e.key === 'Enter') { clearTimeout(debounceTimer); currentPage = 1; searchDiamonds(); }
        });
      }
    });

    /* Select dropdowns (color, clarity, cut) */
    ['dnColorFrom', 'dnColorTo', 'dnClarityFrom', 'dnClarityTo', 'dnCutFrom', 'dnCutTo'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.addEventListener('change', debouncedSearch);
    });

    /* Lab checkboxes */
    $$('.dn-lab-cb').forEach(function (cb) {
      cb.addEventListener('change', debouncedSearch);
    });

    /* Sort */
    var sortEl = document.getElementById('dnSortBy');
    if (sortEl) {
      sortEl.addEventListener('change', function () {
        currentPage = 1;
        searchDiamonds();
      });
    }

    /* Clear all */
    var clearBtn = document.getElementById('dnClearAll');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        $$('.dn-shape-btn.active').forEach(function (b) { b.classList.remove('active'); });
        var caratMin = document.getElementById('dnCaratMin');
        if (caratMin) caratMin.value = '0.30';
        var caratMax = document.getElementById('dnCaratMax');
        if (caratMax) caratMax.value = '';
        var colFrom = document.getElementById('dnColorFrom');
        if (colFrom) colFrom.value = 'D';
        var colTo = document.getElementById('dnColorTo');
        if (colTo) colTo.value = 'M';
        var clrFrom = document.getElementById('dnClarityFrom');
        if (clrFrom) clrFrom.value = 'FL';
        var clrTo = document.getElementById('dnClarityTo');
        if (clrTo) clrTo.value = 'I3';
        var cutFrom = document.getElementById('dnCutFrom');
        if (cutFrom) cutFrom.value = 'Excellent';
        var cutTo = document.getElementById('dnCutTo');
        if (cutTo) cutTo.value = 'Good';
        var priceMin = document.getElementById('dnPriceMin');
        if (priceMin) priceMin.value = '';
        var priceMax = document.getElementById('dnPriceMax');
        if (priceMax) priceMax.value = '';
        $$('.dn-lab-cb').forEach(function (cb) { cb.checked = false; });
        var sort = document.getElementById('dnSortBy');
        if (sort) sort.value = 'Price-Asc';
        currentPage = 1;
        searchDiamonds();
      });
    }

    /* Mobile filter toggle */
    var mobileBtn = document.getElementById('dnMobileFilterBtn');
    var sidebar = document.getElementById('diamondFilterSidebar');
    if (mobileBtn && sidebar) {
      mobileBtn.addEventListener('click', function () {
        sidebar.classList.toggle('mobile-open');
        this.classList.toggle('active');
      });
    }

    /* Modal close */
    var closeBtn = document.getElementById('dnModalClose');
    if (closeBtn) closeBtn.addEventListener('click', closeDetail);

    var overlay = document.getElementById('dnModal');
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === this) closeDetail();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDetail();
    });
  }

  /* ══════════════════════════════════════════════════════
     Inline PDP-style Diamond Detail View
     Renders a full product-page layout inside the
     collection page when ?diamond=ID is in the URL.
     ══════════════════════════════════════════════════════ */

  function buildDetailHTML(d) {
    var shape = d.shape || '';
    var size = d.size || 0;
    var color = d.color || '';
    var clarity = d.clarity || '';
    var cut = d.cut || '';
    var polish = d.polish || '';
    var symmetry = d.symmetry || '';
    var lab = d.lab || '';
    var certNum = d.cert_num || '';
    var price = d.total_sales_price || 0;
    var img = d.image_file || null;
    var depthPct = d.depth_percent;
    var tablePct = d.table_percent;
    var fluorescence = d.fluor_intensity || '';
    var stockNum = d.stock_num || '';
    var v360 = (d.diamond_media && d.diamond_media.length > 0) ? d.diamond_media[0].url : null;

    var measurements = '';
    if (d.meas_length && d.meas_width && d.meas_depth) {
      measurements = d.meas_length + ' x ' + d.meas_width + ' x ' + d.meas_depth + ' mm';
    }

    var title = capitalize(shape) + ' ' + Number(size).toFixed(2) + 'ct Diamond';
    var priceStr = formatPrice(price);

    /* Gallery */
    var galleryHtml = '';
    if (img) {
      galleryHtml = '<div class="pdp-gallery-item dd-main-image">' +
        '<img src="' + img + '" alt="' + title + '" width="600" height="600" ' +
        'onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\'">' +
        '<div class="diamond-placeholder" style="display:none">' + getShapeSVG(shape) + '</div></div>';
    } else if (v360) {
      galleryHtml = '<div class="pdp-gallery-item dd-main-image dd-v360-container">' +
        '<iframe src="' + v360 + '" style="width:100%;height:100%;border:none;" allowfullscreen></iframe></div>';
    } else {
      galleryHtml = '<div class="pdp-gallery-item dd-main-image">' +
        '<div class="diamond-placeholder">' + getShapeSVG(shape) + '</div></div>';
    }
    if (img && v360) {
      galleryHtml += '<div class="pdp-gallery-item dd-v360-container">' +
        '<iframe src="' + v360 + '" style="width:100%;height:100%;border:none;" allowfullscreen></iframe>' +
        '<span class="dd-v360-label">360&deg; View</span></div>';
    }

    /* Quick-spec badges (4C) */
    var badges = [
      { l: 'Carat', v: Number(size).toFixed(2) },
      { l: 'Color', v: color },
      { l: 'Clarity', v: clarity },
      { l: 'Cut', v: cut }
    ];
    var badgeHtml = '';
    for (var b = 0; b < badges.length; b++) {
      if (badges[b].v) {
        badgeHtml += '<div class="dd-spec-badge"><span class="dd-spec-badge-label">' +
          badges[b].l + '</span><span class="dd-spec-badge-value">' + badges[b].v + '</span></div>';
      }
    }

    /* Specs table rows */
    var specRows = [
      ['Shape', capitalize(shape)], ['Carat Weight', Number(size).toFixed(2) + ' ct'],
      ['Color Grade', color], ['Clarity Grade', clarity], ['Cut Grade', cut],
      ['Polish', polish], ['Symmetry', symmetry], ['Fluorescence', fluorescence],
      ['Lab', lab], ['Certificate #', certNum]
    ];
    var specHtml = '';
    for (var s = 0; s < specRows.length; s++) {
      if (specRows[s][1]) {
        specHtml += '<tr><td class="dd-spec-label">' + specRows[s][0] +
          '</td><td class="dd-spec-value">' + specRows[s][1] + '</td></tr>';
      }
    }

    /* Full specs for accordion */
    var allSpecs = [
      ['Shape', capitalize(shape)], ['Carat Weight', Number(size).toFixed(2) + ' ct'],
      ['Color', color], ['Clarity', clarity], ['Cut', cut],
      ['Polish', polish], ['Symmetry', symmetry], ['Fluorescence', fluorescence],
      ['Depth %', depthPct ? depthPct + '%' : ''], ['Table %', tablePct ? tablePct + '%' : ''],
      ['Measurements', measurements], ['Lab', lab], ['Certificate #', certNum], ['Stock #', stockNum]
    ];
    var fullSpecHtml = '';
    for (var a = 0; a < allSpecs.length; a++) {
      if (allSpecs[a][1]) {
        fullSpecHtml += '<div class="pdp-spec"><span class="pdp-spec-label">' + allSpecs[a][0] +
          '</span><span class="pdp-spec-value">' + allSpecs[a][1] + '</span></div>';
      }
    }

    /* Certificate detail text */
    var certDetailHtml = '<p>Certificate details will be provided upon inquiry.</p>';
    if (lab) {
      certDetailHtml = '<p>This diamond has been graded and certified by <strong>' + lab + '</strong>';
      if (certNum) certDetailHtml += ' with certificate number <strong>' + certNum + '</strong>';
      certDetailHtml += '.</p>';
      if (lab === 'GIA') certDetailHtml += '<p><a href="https://www.gia.edu/report-check" target="_blank" rel="noopener">Verify on GIA.edu</a></p>';
      else if (lab === 'IGI') certDetailHtml += '<p><a href="https://www.igi.org/verify-your-report" target="_blank" rel="noopener">Verify on IGI.org</a></p>';
    }

    /* Inquire URL */
    var subj = encodeURIComponent('Inquiry: ' + title);
    var bodyText = encodeURIComponent(
      'I am interested in the following diamond:\n\n' + title + '\n' +
      'Color: ' + color + ' | Clarity: ' + clarity + ' | Cut: ' + cut + '\n' +
      (lab ? 'Lab: ' + lab : '') + (certNum ? ' | Cert: ' + certNum : '') + '\n' +
      (stockNum ? 'Stock #: ' + stockNum + '\n' : '') +
      '\nPlease provide more details and pricing.'
    );
    var inquireHref = '/pages/contact?subject=' + subj + '&body=' + bodyText;

    /* Back URL */
    var backUrl = window.location.pathname;

    return '' +
      /* Breadcrumb */
      '<div class="container">' +
        '<nav class="breadcrumb">' +
          '<a href="/">Home</a><span class="separator">/</span>' +
          '<a href="' + backUrl + '">Diamonds</a><span class="separator">/</span>' +
          '<span class="current">' + capitalize(shape) + ' ' + Number(size).toFixed(2) + 'ct</span>' +
        '</nav>' +
      '</div>' +

      /* PDP Section */
      '<section class="product-detail">' +
        '<div class="container">' +

          /* Back to results link */
          '<a href="' + backUrl + '" class="dd-back-link">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>' +
            ' Back to Diamonds' +
          '</a>' +

          '<div class="product-detail-grid">' +

            /* LEFT: Gallery */
            '<div class="pdp-gallery"><div class="pdp-gallery-grid dd-gallery-grid">' +
              galleryHtml +
            '</div></div>' +

            /* RIGHT: Info */
            '<div class="pdp-info">' +
              '<h1 class="pdp-title">' + title + '</h1>' +
              '<div class="pdp-subtitle">' +
                (color ? color + ' Color' : '') +
                (clarity ? ' &middot; ' + clarity + ' Clarity' : '') +
                (cut ? ' &middot; ' + cut + ' Cut' : '') +
              '</div>' +
              '<div class="pdp-price">' + priceStr + '</div>' +

              /* 4C Badges */
              '<div class="dd-quick-specs">' + badgeHtml + '</div>' +

              /* Specs Table */
              '<div class="dd-specs-table-wrap"><table class="dd-specs-table"><tbody>' +
                specHtml +
              '</tbody></table></div>' +

              /* Certificate */
              (lab ? '<div class="dd-certificate">' +
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>' +
                '<span>' + lab + ' Certified' + (certNum ? ' &mdash; #' + certNum : '') + '</span>' +
              '</div>' : '') +

              /* Inquire Button */
              '<a href="' + inquireHref + '" class="pdp-add-to-bag dd-inquire-btn">INQUIRE ABOUT THIS DIAMOND</a>' +

              /* Secondary Actions */
              '<div class="pdp-secondary-actions">' +
                '<button class="pdp-action-btn pdp-drop-hint" onclick="window.location.href=\'/pages/contact?subject=' + encodeURIComponent('Hint: ' + title) + '\'">' +
                  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' +
                  ' Drop a Hint</button>' +
                '<button class="pdp-action-btn pdp-wishlist-btn">' +
                  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
                  ' Add to Wishlist</button>' +
              '</div>' +

              /* Delivery */
              '<div class="pdp-delivery">' +
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>' +
                '<span>Free FedEx 2-Day Shipping on all orders</span>' +
              '</div>' +

              /* Trust Badges */
              '<div class="pdp-trust">' +
                '<h4 class="pdp-trust-title">We\'ve Got You Covered</h4>' +
                '<div class="pdp-trust-items">' +
                  '<div class="pdp-trust-item"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="1.5"><rect x="1" y="3" width="22" height="18" rx="2"/><path d="M1 9h22"/></svg><span>Free Shipping &amp; Returns</span></div>' +
                  '<div class="pdp-trust-item"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg><span>Certified &amp; Conflict-Free</span></div>' +
                  '<div class="pdp-trust-item"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 12 A4 4 0 0 1 16 12"/></svg><span>Expert Diamond Consultation</span></div>' +
                '</div>' +
              '</div>' +

            '</div>' + /* /pdp-info */
          '</div>' + /* /product-detail-grid */
        '</div>' +
      '</section>' +

      /* Accordions */
      '<section class="pdp-details-section"><div class="container">' +
        '<div class="pdp-accordion"><button class="pdp-accordion-trigger dd-accordion-btn active"><span>Diamond Details</span>' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg></button>' +
          '<div class="pdp-accordion-content open"><div class="rte"><p>This certified loose diamond has been evaluated and graded by an independent gemological laboratory.</p></div>' +
          '<div class="pdp-specs-grid">' + fullSpecHtml + '</div></div></div>' +

        '<div class="pdp-accordion"><button class="pdp-accordion-trigger dd-accordion-btn"><span>Certificate &amp; Grading</span>' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg></button>' +
          '<div class="pdp-accordion-content">' + certDetailHtml + '</div></div>' +

        '<div class="pdp-accordion"><button class="pdp-accordion-trigger dd-accordion-btn"><span>Your Order Includes</span>' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg></button>' +
          '<div class="pdp-accordion-content"><ul class="pdp-includes-list">' +
            '<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Free FedEx 2-Day Shipping</li>' +
            '<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Independent Lab Certificate</li>' +
            '<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Professional Appraisal</li>' +
            '<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Secure &amp; Discreet Packaging</li>' +
            '<li><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b714a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> 30-Day Return Policy</li>' +
          '</ul></div></div>' +

        '<div class="pdp-accordion"><button class="pdp-accordion-trigger dd-accordion-btn"><span>Shipping &amp; Returns</span>' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg></button>' +
          '<div class="pdp-accordion-content"><p>All orders ship free via FedEx 2-Day with full insurance. Free 30-day returns for a full refund or exchange. <a href="/pages/shipping-returns">Learn more</a></p></div></div>' +
      '</div></section>' +

      /* Showroom */
      '<section class="pdp-showroom"><div class="container"><div class="pdp-showroom-card">' +
        '<div class="pdp-showroom-info"><h3>Visit a Showroom</h3>' +
          '<p class="pdp-showroom-loc"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Los Angeles, CA</p>' +
          '<p class="pdp-showroom-hours">Mon-Sat: 10am-7pm &bull; Sun: 11am-6pm</p>' +
          '<div class="pdp-showroom-actions"><a href="/pages/appointments" class="btn btn-primary btn-sm">Book an Appointment</a>' +
          '<a href="/pages/locations" class="pdp-showroom-other">View All Locations</a></div></div>' +
        '<div class="pdp-showroom-contact"><h4>Need Help?</h4>' +
          '<a href="/pages/contact"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> Email Us</a>' +
          '<a href="tel:8005550199"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg> 800.555.0199</a>' +
          '<a href="/pages/locations"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Visit Los Angeles</a>' +
        '</div></div></div></section>';
  }

  /* Show the inline detail view, hiding the grid */
  function showInlineDetail(d) {
    var wrapper = document.getElementById('rapnetDiamonds');
    if (!wrapper) return;

    /* Update page title */
    var shape = d.shape || '';
    var size = d.size || 0;
    var title = capitalize(shape) + ' ' + Number(size).toFixed(2) + 'ct Diamond';
    document.title = title + ' — LA Diamond';

    /* Replace wrapper content with PDP layout */
    wrapper.innerHTML = buildDetailHTML(d);

    /* Set up accordions */
    var triggers = wrapper.querySelectorAll('.dd-accordion-btn');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function () {
        var content = this.nextElementSibling;
        var isOpen = this.classList.contains('active');
        /* Close all in this section */
        for (var j = 0; j < triggers.length; j++) {
          triggers[j].classList.remove('active');
          triggers[j].nextElementSibling.classList.remove('open');
        }
        if (!isOpen) {
          this.classList.add('active');
          content.classList.add('open');
        }
      });
    }

    /* Scroll to top */
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* Handle browser back button */
  window.addEventListener('popstate', function () {
    var params = new URLSearchParams(window.location.search);
    if (!params.get('diamond')) {
      /* Returning to grid — reload the page to restore grid state */
      window.location.reload();
    }
  });

  /* Check URL for ?diamond=ID on page load */
  function checkDetailParam() {
    var params = new URLSearchParams(window.location.search);
    var diamondId = params.get('diamond');
    if (!diamondId) return false;

    /* Try sessionStorage first */
    var storageKey = 'rapnet_diamond_' + diamondId;
    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(storageKey)); } catch (e) { /* ignore */ }

    if (cached) {
      showInlineDetail(cached);
      return true;
    }

    /* Fallback: fetch from API */
    var wrapper = document.getElementById('rapnetDiamonds');
    if (wrapper) {
      wrapper.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;min-height:400px;text-align:center;">' +
        '<div><svg width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="16" fill="none" stroke="#8b714a" stroke-width="2" stroke-dasharray="80" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="1s" repeatCount="indefinite"/></circle></svg>' +
        '<p style="font-family:Jost,sans-serif;font-size:14px;color:#888;margin-top:12px;">Loading diamond details...</p></div></div>';
    }

    getToken().then(function (token) {
      return fetch(RAPNET_DIAMONDS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ request: { header: {}, body: { diamond_id: parseInt(diamondId), page_number: 1, page_size: 1, search_type: 'WHITE' } } })
      });
    })
    .then(function (resp) { if (!resp.ok) throw new Error('HTTP ' + resp.status); return resp.json(); })
    .then(function (data) {
      if (data.response && data.response.body && data.response.body.diamonds && data.response.body.diamonds.length > 0) {
        showInlineDetail(data.response.body.diamonds[0]);
      } else {
        throw new Error('Diamond not found');
      }
    })
    .catch(function () {
      if (wrapper) {
        wrapper.innerHTML = '<div style="text-align:center;padding:60px 20px;">' +
          '<p style="font-family:Jost,sans-serif;font-size:16px;color:#888;">Diamond not found or unavailable.</p>' +
          '<a href="' + window.location.pathname + '" style="color:#8b714a;margin-top:12px;display:inline-block;">Browse All Diamonds</a></div>';
      }
    });

    return true;
  }

  /* ── Init ───────────────────────────────────────────── */
  function init() {
    /* Check for detail view first — if ?diamond=ID is present, show PDP instead of grid */
    if (checkDetailParam()) return;

    $grid = document.getElementById('dnGrid');
    $pagination = document.getElementById('dnPagination');
    $resultsCount = document.getElementById('dnResultsCount');
    $sortBy = document.getElementById('dnSortBy');
    $modal = document.getElementById('dnModal');

    setupFilterListeners();

    /* Pre-select shape from URL ?shape=Oval */
    var shapeParam = new URLSearchParams(window.location.search).get('shape');
    if (shapeParam) {
      var shapeBtn = document.querySelector('.dn-shape-btn[data-shape="' + shapeParam + '"]');
      if (shapeBtn) shapeBtn.classList.add('active');
    }

    searchDiamonds();
  }

  /* Public API for external access */
  window.__rapnet = {
    search: searchDiamonds,
    showDetail: function (id) {
      if (diamonds[id]) showDetail(diamonds[id]);
    }
  };

  /* Start */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
